import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useConversation, useConversationClientTool } from '@elevenlabs/react'
import { useWakeLock } from '../lib/useWakeLock'
import { useVisibilityPause } from '../lib/useVisibilityPause'
import { t, FIRST_MESSAGE_BY_LOCALE, persistLocale } from '../i18n'
import {
  lookupFlight,
  lookupBooking,
  changeFlight,
  sendConfirmation,
} from '../lib/api'
import ConnectionBanner from './ConnectionBanner'
import { StopIcon, ChevronDownIcon, CheckIcon, LiveDot } from './Icons'

const AGENT_ID =
  import.meta.env.VITE_ELEVENLABS_CORENDON_AGENT_ID ||
  // Empty fallback: when unset, startSession will surface a visible error
  // so the misconfiguration is obvious (rather than silently failing).
  ''

const RESUME_STATE_KEY = 'corendon_resume_state'
const LANG_OPTIONS = [
  { code: 'nl', label: 'NL', full: 'Nederlands' },
  { code: 'en', label: 'EN', full: 'English' },
  { code: 'tr', label: 'TR', full: 'Türkçe' },
]

function readResumeState() {
  try {
    const raw = sessionStorage.getItem(RESUME_STATE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function writeResumeState(state) {
  try {
    if (state) sessionStorage.setItem(RESUME_STATE_KEY, JSON.stringify(state))
    else sessionStorage.removeItem(RESUME_STATE_KEY)
  } catch {
    /* ignore */
  }
}

export default function CorendonWidget({ locale, onLocaleChange, userEmail }) {
  const [bannerKind, setBannerKind] = useState(null) // 'paused' | 'resumed' | null
  const [errorMsg, setErrorMsg] = useState(null)
  const [langOpen, setLangOpen] = useState(false)
  const lastTopicRef = useRef(null)
  const activePnrRef = useRef(null)
  // The outer wrapper that contains BOTH the chip and the popover. Outside-click
  // detection must check against this wrapper, not the chip alone, otherwise
  // clicks on popover items get classified as "outside" and the popover closes
  // before the React onClick can fire.
  const dropdownRef = useRef(null)
  const { acquire: acquireWakeLock, release: releaseWakeLock } = useWakeLock()

  const conversation = useConversation({
    onError: (err) => {
      console.error('[corendon] conversation error', err)
      setErrorMsg(t(locale, 'errorGeneric'))
    },
    onMessage: (msg) => {
      if (msg?.source === 'ai' && typeof msg?.message === 'string') {
        lastTopicRef.current = msg.message.slice(0, 280)
      }
    },
  })

  const status = conversation.status
  const isConnected = status === 'connected'
  const isConnecting = status === 'connecting'

  useConversationClientTool('lookup_flight', async (params) => {
    try {
      return JSON.stringify(
        await lookupFlight({ flight_number: params.flight_number, date: params.date }),
      )
    } catch (e) {
      return JSON.stringify({ error: e.message || 'lookup_failed' })
    }
  })

  useConversationClientTool('lookup_booking', async (params) => {
    try {
      const out = await lookupBooking({
        surname: params.surname,
        dob: params.dob,
        pnr: params.pnr,
      })
      if (out?.pnr) activePnrRef.current = out.pnr
      return JSON.stringify(out)
    } catch (e) {
      return JSON.stringify({ error: e.message || 'lookup_failed' })
    }
  })

  useConversationClientTool('change_flight', async (params) => {
    try {
      return JSON.stringify(
        await changeFlight({
          pnr: params.pnr,
          surname: params.surname,
          dob: params.dob,
          new_flight_date: params.new_flight_date,
          new_flight_number: params.new_flight_number,
        }),
      )
    } catch (e) {
      return JSON.stringify({ error: e.message || 'change_failed' })
    }
  })

  useConversationClientTool('send_confirmation', async (params) => {
    try {
      return JSON.stringify(
        await sendConfirmation({
          pnr: params.pnr,
          email: params.email,
          language: params.language || locale,
          kind: params.kind || 'change',
        }),
      )
    } catch (e) {
      return JSON.stringify({ error: e.message || 'send_failed' })
    }
  })

  // Outside-click closes the popover. We listen on `click` (not `mousedown`)
  // so the React onClick on a popover item gets a chance to run first —
  // mousedown closes the popover before click can fire, unmounting the item
  // and dropping its click handler.
  useEffect(() => {
    if (!langOpen) return
    function onDocClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setLangOpen(false)
      }
    }
    document.addEventListener('click', onDocClick)
    return () => {
      document.removeEventListener('click', onDocClick)
    }
  }, [langOpen])

  const startSession = useCallback(
    async ({ resuming = false } = {}) => {
      if (!AGENT_ID) {
        setErrorMsg(
          'VITE_ELEVENLABS_CORENDON_AGENT_ID is not configured for this build.',
        )
        return
      }
      setErrorMsg(null)
      try {
        try {
          await navigator.mediaDevices.getUserMedia({ audio: true })
        } catch {
          /* SDK will surface the real reason */
        }

        const firstMessage = resuming ? null : FIRST_MESSAGE_BY_LOCALE[locale] || FIRST_MESSAGE_BY_LOCALE.nl
        const overrides = firstMessage
          ? { agent: { firstMessage, language: locale } }
          : { agent: { language: locale } }

        await conversation.startSession({
          agentId: AGENT_ID,
          connectionType: 'webrtc',
          overrides,
          dynamicVariables: {
            user_language: locale,
            user_email_hint: userEmail || '',
          },
        })

        await acquireWakeLock()

        if (resuming) {
          const prior = readResumeState()
          if (prior?.lastTopic) {
            try {
              conversation.sendContextualUpdate(
                `[system] The user briefly backgrounded the browser tab and returned. Continue in ${prior.language || locale}. Last topic was: "${prior.lastTopic}". Active booking PNR: ${prior.activePnr || 'none'}.`,
              )
            } catch {
              /* not ready yet — fine */
            }
            setBannerKind('resumed')
            setTimeout(() => setBannerKind(null), 4000)
          }
          writeResumeState(null)
        }
      } catch (e) {
        console.error('[corendon] startSession failed', e)
        setErrorMsg(t(locale, 'errorGeneric'))
      }
    },
    [conversation, locale, userEmail, acquireWakeLock],
  )

  const endSession = useCallback(async () => {
    try {
      await conversation.endSession()
    } catch {
      /* ignore */
    }
    await releaseWakeLock()
    writeResumeState(null)
    lastTopicRef.current = null
    activePnrRef.current = null
  }, [conversation, releaseWakeLock])

  useVisibilityPause(
    useCallback(
      async (hidden) => {
        if (hidden) {
          if (status === 'connected' || status === 'connecting') {
            writeResumeState({
              language: locale,
              lastTopic: lastTopicRef.current,
              activePnr: activePnrRef.current,
              when: Date.now(),
            })
            try {
              await conversation.endSession()
            } catch {
              /* ignore */
            }
            await releaseWakeLock()
            setBannerKind('paused')
          }
        } else {
          const prior = readResumeState()
          if (prior && Date.now() - prior.when < 30 * 60 * 1000) {
            void startSession({ resuming: true })
          }
        }
      },
      [status, locale, conversation, releaseWakeLock, startSession],
    ),
  )

  useEffect(() => {
    return () => {
      void releaseWakeLock()
    }
  }, [releaseWakeLock])

  const pickLocale = (next) => {
    onLocaleChange(next)
    persistLocale(next)
    setLangOpen(false)
  }

  const activeLang = LANG_OPTIONS.find((l) => l.code === locale) || LANG_OPTIONS[0]

  return (
    <div className="flex w-full flex-col gap-3">
      {/* Composite start button + language selector.
          Layout: [ ● Start gesprek ─────── NL ▾ ]
          A button (start/stop) and a sibling div (language chip) sit inside a
          shared rounded container so they look like one element. Using a real
          <button> wrapping <span role="button"> for the chip would be invalid
          HTML, so we split them as siblings styled to read as one. */}
      <div ref={dropdownRef} className="relative w-full">
        <div
          className={
            'group relative flex w-full items-stretch overflow-hidden rounded-2xl shadow-sm ring-1 transition-all ' +
            (isConnected
              ? 'bg-white text-corendon-red ring-corendon-red/30'
              : 'bg-corendon-red text-white ring-transparent hover:bg-corendon-red-dark hover:shadow-md') +
            (isConnecting ? ' opacity-80' : '')
          }
        >
          <button
            type="button"
            onClick={isConnected ? endSession : () => startSession()}
            disabled={isConnecting}
            aria-pressed={isConnected}
            className="flex flex-1 items-center gap-3.5 px-5 py-4 text-left text-[15px] font-semibold tracking-tight focus:outline-none focus-visible:bg-corendon-red-dark"
          >
            {isConnected ? (
              <StopIcon className="h-4 w-4" />
            ) : isConnecting ? (
              <span
                className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
                aria-hidden
              />
            ) : (
              <LiveDot className="text-white" />
            )}
            <span className="leading-none">
              {isConnecting
                ? t(locale, 'loading')
                : isConnected
                  ? t(locale, 'endButton')
                  : t(locale, 'startButton')}
            </span>
          </button>

          {/* Language chip — only shown when idle. Hidden during connecting/connected. */}
          {!isConnected && !isConnecting && (
            <div className="flex items-center border-l border-white/20">
              <button
                type="button"
                onClick={() => setLangOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={langOpen}
                aria-label="Change language"
                className="flex h-full items-center gap-1.5 px-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/90 transition hover:text-white focus:outline-none focus-visible:bg-white/10"
              >
                <span>{activeLang.label}</span>
                <ChevronDownIcon
                  className={'h-3 w-3 transition-transform ' + (langOpen ? 'rotate-180' : '')}
                />
              </button>
            </div>
          )}
        </div>

        {/* Language popover — full-width on mobile, anchored right on desktop */}
        {langOpen && !isConnected && (
          <div
            role="listbox"
            aria-label="Language"
            className="absolute inset-x-0 top-full z-10 mt-2 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg sm:left-auto sm:right-0 sm:w-56"
          >
            {LANG_OPTIONS.map((opt) => {
              const active = opt.code === locale
              return (
                <button
                  key={opt.code}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => pickLocale(opt.code)}
                  className={
                    'flex w-full items-center justify-between gap-4 px-4 py-3 text-left text-[13px] transition ' +
                    (active
                      ? 'bg-corendon-red/5 text-corendon-red'
                      : 'text-gray-800 hover:bg-gray-50')
                  }
                >
                  <span className="flex items-center gap-3">
                    <span className="w-6 font-mono text-[10px] uppercase tracking-[0.16em] text-gray-400">
                      {opt.label}
                    </span>
                    <span className="font-medium">{opt.full}</span>
                  </span>
                  {active && <CheckIcon className="h-3.5 w-3.5 text-corendon-red" />}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Idle hint under the button */}
      {!isConnected && !isConnecting && (
        <p className="text-[11px] text-gray-400">
          {t(locale, 'micHint')}
        </p>
      )}

      {errorMsg && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {errorMsg}
        </div>
      )}

      <ConnectionBanner
        kind={bannerKind}
        locale={locale}
        onDismiss={() => setBannerKind(null)}
      />
    </div>
  )
}
